import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface PageTab {
  id: string;
  label: string;
  /** Shown after the label once known, e.g. the number of tables. */
  count?: number | string | undefined;
}

interface PageTabsProps {
  /** Accessible name of the tab list, e.g. "Stripe data sections". */
  label: string;
  tabs: readonly PageTab[];
  value: string;
  onChange: (id: string) => void;
  /** Blocks of the selected tab. */
  children: ReactNode;
}

/** Keys that move between tabs, to the index they move to. */
const MOVES: Record<string, (index: number, last: number) => number> = {
  ArrowRight: (index, last) => (index === last ? 0 : index + 1),
  ArrowLeft: (index, last) => (index === 0 ? last : index - 1),
  Home: () => 0,
  End: (_, last) => last,
};

/**
 * Sections of an overview page under its tile row: the prototype's `.tabs[role=tablist]`, left
 * aligned (`.tabs--page`), and one `role=tabpanel` with the selected tab's blocks. Arrow keys,
 * Home and End move between the tabs.
 */
export function PageTabs({ label, tabs, value, onChange, children }: PageTabsProps) {
  const prefix = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const tabId = (id: string) => `${prefix}-tab-${id}`;
  const panelId = `${prefix}-panel`;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const move = MOVES[event.key];
    if (!move || tabs.length === 0) return;
    event.preventDefault();
    const index = move(
      Math.max(
        0,
        tabs.findIndex((tab) => tab.id === value),
      ),
      tabs.length - 1,
    );
    const tab = tabs[index];
    if (!tab) return;
    onChange(tab.id);
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[index]?.focus();
  };

  return (
    <>
      <div
        ref={listRef}
        className="tabs tabs--page"
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={tabId(tab.id)}
            className="tab"
            type="button"
            role="tab"
            aria-selected={tab.id === value}
            aria-controls={panelId}
            tabIndex={tab.id === value ? 0 : -1}
            onClick={() => {
              onChange(tab.id);
            }}
          >
            <span>{tab.label}</span>
            {/* The space keeps the accessible name "Tables 12"; the flex row drops it visually. */}
            {tab.count !== undefined && (
              <>
                {' '}
                <small className="tab__count">{tab.count}</small>
              </>
            )}
          </button>
        ))}
      </div>
      <div className="tab-panel" id={panelId} role="tabpanel" aria-labelledby={tabId(value)}>
        {children}
      </div>
    </>
  );
}
